import React, { type ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import TheaterComedyOutlinedIcon from '@mui/icons-material/TheaterComedyOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import FaceRetouchingNaturalOutlinedIcon from '@mui/icons-material/FaceRetouchingNaturalOutlined';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import CheckroomOutlinedIcon from '@mui/icons-material/CheckroomOutlined';
import AnimationOutlinedIcon from '@mui/icons-material/AnimationOutlined';
import DirectionsRunOutlinedIcon from '@mui/icons-material/DirectionsRunOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import MicOutlinedIcon from '@mui/icons-material/MicOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import type { ContestSelectOption } from './contestSelectOptions';

export interface ContestTypePreset {
    value: string;
    label: string;
    subtitle: string;
    Icon: ComponentType<SvgIconProps>;
    criteria: string[];
}

export const JUDGING_CONTEST_TYPES: ContestTypePreset[] = [
    {
        value: 'acting',
        label: 'Acting',
        subtitle: 'Performance, presence, and character work',
        Icon: TheaterComedyOutlinedIcon,
        criteria: [
            'Character believability and motivation',
            'Emotional range and authenticity',
            'Vocal delivery, diction, and timing',
            'Physicality, blocking, and body language',
            'Chemistry and scene partner connection',
            'Overall screen presence and star quality',
        ],
    },
    {
        value: 'short_film',
        label: 'Short Film',
        subtitle: 'Narrative filmmaking and craft',
        Icon: MovieOutlinedIcon,
        criteria: [
            'Story structure and narrative impact',
            'Directing vision and creative choices',
            'Cinematography, lighting, and composition',
            'Editing, pacing, and rhythm',
            'Sound design, score, and mix',
            'Production value and polish',
        ],
    },
    {
        value: 'makeup',
        label: 'Makeup & SFX',
        subtitle: 'Beauty, character, and effects makeup',
        Icon: FaceRetouchingNaturalOutlinedIcon,
        criteria: [
            'Concept originality and creative vision',
            'Application technique and blending',
            'Color theory and skin tone matching',
            'Character transformation believability',
            'Prosthetics, SFX, and special materials',
            'On-camera consistency and longevity',
        ],
    },
    {
        value: 'music_video',
        label: 'Music Video',
        subtitle: 'Visual storytelling for music',
        Icon: MusicNoteOutlinedIcon,
        criteria: [
            'Alignment with track mood and lyrics',
            'Visual concept and art direction',
            'Performance energy and choreography',
            'Cinematography and lighting design',
            'Editing sync to music and beats',
            'Overall impact and memorability',
        ],
    },
    {
        value: 'photography',
        label: 'Photography',
        subtitle: 'Still image composition and craft',
        Icon: PhotoCameraOutlinedIcon,
        criteria: [
            'Composition and framing',
            'Lighting and exposure control',
            'Color grading and tonal mood',
            'Subject focus and storytelling',
            'Technical sharpness and detail',
            'Originality and creative voice',
        ],
    },
    {
        value: 'fashion',
        label: 'Fashion',
        subtitle: 'Style, runway, and editorial looks',
        Icon: CheckroomOutlinedIcon,
        criteria: [
            'Styling concept and cohesion',
            'Garment fit, layering, and silhouette',
            'Use of color, texture, and accessories',
            'Model presence and attitude',
            'Brand or brief alignment',
            'Overall editorial impact',
        ],
    },
    {
        value: 'animation',
        label: 'Animation',
        subtitle: 'Motion design and animated storytelling',
        Icon: AnimationOutlinedIcon,
        criteria: [
            'Character design and appeal',
            'Animation quality and fluidity',
            'Story clarity and pacing',
            'Backgrounds, layout, and world-building',
            'Sound and music integration',
            'Technical execution and rendering',
        ],
    },
    {
        value: 'dance',
        label: 'Dance',
        subtitle: 'Movement, choreography, and performance',
        Icon: DirectionsRunOutlinedIcon,
        criteria: [
            'Technical skill and precision',
            'Musicality and timing',
            'Choreography originality',
            'Stage presence and expression',
            'Formation, spacing, and group sync',
            'Costume and visual presentation',
        ],
    },
    {
        value: 'documentary',
        label: 'Documentary',
        subtitle: 'Non-fiction storytelling',
        Icon: VideocamOutlinedIcon,
        criteria: [
            'Subject matter relevance and depth',
            'Research, accuracy, and ethics',
            'Interview and narrative structure',
            'Cinematography and b-roll usage',
            'Editing and information pacing',
            'Emotional resonance and impact',
        ],
    },
    {
        value: 'voiceover',
        label: 'Voiceover',
        subtitle: 'Narration, dubbing, and vocal performance',
        Icon: MicOutlinedIcon,
        criteria: [
            'Vocal clarity and articulation',
            'Tone, pacing, and delivery',
            'Character or brand fit',
            'Emotional engagement',
            'Recording quality and mix',
            'Script interpretation',
        ],
    },
    {
        value: 'commercial',
        label: 'Commercial / Ad',
        subtitle: 'Brand-focused creative advertising',
        Icon: CampaignOutlinedIcon,
        criteria: [
            'Brand message clarity',
            'Creative concept and hook',
            'Product or service showcase',
            'Production quality',
            'Call-to-action effectiveness',
            'Memorability and shareability',
        ],
    },
];

export function getContestTypePreset(type: string): ContestTypePreset | undefined {
    return JUDGING_CONTEST_TYPES.find(t => t.value === type);
}

export function getContestTypeSelectOptions(): ContestSelectOption[] {
    return JUDGING_CONTEST_TYPES.map(t => ({
        value: t.value,
        label: t.label,
        subtitle: t.subtitle,
        icon: React.createElement(t.Icon),
    }));
}

export function getCriteriaOptionsForContestType(type: string): Array<{ value: string; label: string }> {
    const preset = getContestTypePreset(type);
    if (!preset) return [];
    return preset.criteria.map((label, i) => ({
        value: `criterion_${i}`,
        label,
    }));
}

export function criteriaLabelsFromSelection(type: string, selectedValues: string[]): string[] {
    const preset = getContestTypePreset(type);
    if (!preset) return [];
    return selectedValues
        .map(v => {
            const idx = parseInt(v.replace('criterion_', ''), 10);
            return Number.isNaN(idx) ? '' : preset.criteria[idx] ?? '';
        })
        .filter(Boolean);
}

export function defaultCriteriaSelection(type: string): string {
    return getCriteriaOptionsForContestType(type)
        .map(o => o.value)
        .join(',');
}
